import React, { useRef, useCallback } from "react";

/**
 * Generates short, friendly tones with the WebAudio API instead of
 * shipping .mp3/.wav assets — keeps the bundle tiny and avoids any
 * licensing/attribution overhead for sound effects.
 *
 * One shared AudioContext is created lazily on first user interaction
 * (required by mobile browsers) and reused for the whole session.
 */
let sharedCtx = null;
function getCtx() {
  if (!sharedCtx) {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    sharedCtx = new AudioCtx();
  }
  if (sharedCtx.state === "suspended") sharedCtx.resume();
  return sharedCtx;
}

function playTone({ freq, duration = 0.15, type = "sine", volume = 0.18, delay = 0 }) {
  try {
    const ctx = getCtx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = type;
    osc.frequency.value = freq;
    gain.gain.setValueAtTime(0.0001, ctx.currentTime + delay);
    gain.gain.exponentialRampToValueAtTime(volume, ctx.currentTime + delay + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + delay + duration);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(ctx.currentTime + delay);
    osc.stop(ctx.currentTime + delay + duration + 0.05);
  } catch (e) {
    // Silently no-op if audio isn't available (e.g. autoplay policy) —
    // sound is enhancement only, never blocks gameplay.
  }
}

export function useTinyThinkSound(enabled = true) {
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;

  const tap = useCallback(() => {
    if (!enabledRef.current) return;
    playTone({ freq: 520, duration: 0.08, type: "sine", volume: 0.15 });
  }, []);

  const correct = useCallback(() => {
    if (!enabledRef.current) return;
    playTone({ freq: 660, duration: 0.12, type: "triangle", volume: 0.2 });
    playTone({ freq: 880, duration: 0.15, type: "triangle", volume: 0.18, delay: 0.1 });
  }, []);

  const wrong = useCallback(() => {
    if (!enabledRef.current) return;
    playTone({ freq: 220, duration: 0.2, type: "sine", volume: 0.15 });
  }, []);

  const levelComplete = useCallback(() => {
    if (!enabledRef.current) return;
    [523, 659, 784, 1046].forEach((f, i) =>
      playTone({ freq: f, duration: 0.18, type: "triangle", volume: 0.2, delay: i * 0.09 })
    );
  }, []);

  const surpriseBox = useCallback(() => {
    if (!enabledRef.current) return;
    [400, 500, 650, 800, 1000].forEach((f, i) =>
      playTone({ freq: f, duration: 0.1, type: "square", volume: 0.12, delay: i * 0.05 })
    );
  }, []);

  const streakUp = useCallback(() => {
    if (!enabledRef.current) return;
    playTone({ freq: 784, duration: 0.12, type: "sine", volume: 0.2 });
    playTone({ freq: 1046, duration: 0.2, type: "sine", volume: 0.2, delay: 0.12 });
  }, []);

  return { tap, correct, wrong, levelComplete, surpriseBox, streakUp };
}
